import { useEffect, useState, useCallback } from 'react';

const CURRENT_VERSION = "2.13.1"; // v2.13.1: sliding sessions, shared cookie domain, clearer AI-tool session message
const VERSION_CHECK_INTERVAL = 60000; // Check every 60 seconds (1 minute)
const LAST_VERSION_KEY = 'app_last_version';
const LAST_CHECK_KEY = 'app_last_check';
const UPDATE_IN_PROGRESS_KEY = 'app_update_in_progress';
const SILENT_MODE = true; // Silent background sync - no user notifications
const MAX_UPDATE_ATTEMPTS = 3; // Prevent infinite loops

export function useAutoUpdate(API_URL) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const performUpdate = useCallback((targetVersion) => {
    // Check if we're already updating (prevent infinite loops)
    const updateInProgress = localStorage.getItem(UPDATE_IN_PROGRESS_KEY);
    if (updateInProgress) {
      const timestamp = parseInt(updateInProgress);
      const timeSinceUpdate = Date.now() - timestamp;
      
      // If update was initiated less than 10 seconds ago, skip
      if (timeSinceUpdate < 10000) {
        console.log('[Silent-Sync] Update already in progress, skipping...');
        return;
      }
    }
    
    // Mark update as in progress
    localStorage.setItem(UPDATE_IN_PROGRESS_KEY, Date.now().toString());
    
    if (SILENT_MODE) {
      console.log('[Silent-Sync] New version detected, reloading...');
    } else {
      setIsUpdating(true);
      console.log('[Auto-Update] Performing update reload...');
    }
    
    try {
      // Clear all caches silently
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }

      // Unregister all service workers silently (EXCEPT the push notifications SW)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            const scriptURL = (registration.active && registration.active.scriptURL) || '';
            if (!scriptURL.includes('push-sw.js')) {
              registration.unregister();
            }
          });
        });
      }

      // Clear session storage (but keep localStorage for version tracking)
      sessionStorage.clear();

      // ANTI-LOOP: Record the SERVER version we are updating TO (not the current bundle version).
      // After reload, even if the browser still serves the old bundle, lastKnownVersion === serverVersion
      // so checkVersion will NOT trigger another reload (breaks the infinite loop).
      localStorage.setItem(LAST_VERSION_KEY, targetVersion || CURRENT_VERSION);
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());

      // Silent reload - immediate, no delay
      setTimeout(() => {
        // Clear update flag right before reload
        localStorage.removeItem(UPDATE_IN_PROGRESS_KEY);
        window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
      }, 100);
    } catch (error) {
      console.error('[Silent-Sync] Error during sync:', error);
      localStorage.removeItem(UPDATE_IN_PROGRESS_KEY);
      window.location.reload(true);
    }
  }, []);

  const checkVersion = useCallback(async () => {
    // Prevent checking if update is in progress
    const updateInProgress = localStorage.getItem(UPDATE_IN_PROGRESS_KEY);
    if (updateInProgress) {
      const timestamp = parseInt(updateInProgress);
      const timeSinceUpdate = Date.now() - timestamp;
      
      if (timeSinceUpdate < 10000) {
        console.log('[Silent-Sync] Update in progress, skipping version check');
        return;
      } else {
        // Clear stale flag
        localStorage.removeItem(UPDATE_IN_PROGRESS_KEY);
      }
    }
    
    try {
      const response = await fetch(`${API_URL}/api/version?_=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const serverVersion = data.version;
        const lastKnownVersion = localStorage.getItem(LAST_VERSION_KEY);
        const now = Date.now();

        if (SILENT_MODE) {
          console.log(`[Silent-Sync] Current: ${CURRENT_VERSION}, Server: ${serverVersion}, Last Known: ${lastKnownVersion}`);
        }

        // Update check timestamp
        localStorage.setItem(LAST_CHECK_KEY, now.toString());

        // ONLY update if we haven't already attempted an update to this exact server version
        if (serverVersion !== CURRENT_VERSION && lastKnownVersion !== serverVersion) {
          console.log('[Silent-Sync] NEW VERSION DETECTED! Syncing now...');
          setUpdateAvailable(true);
          
          // Update immediately, recording the target version to prevent reload loops
          performUpdate(serverVersion);
        } 
        // If versions match, just update the marker
        else if (serverVersion === CURRENT_VERSION) {
          localStorage.setItem(LAST_VERSION_KEY, CURRENT_VERSION);
          
          // Clear update flag if it exists
          localStorage.removeItem(UPDATE_IN_PROGRESS_KEY);
        }
      }
    } catch (error) {
      // Silent error logging - don't disturb user
      console.error('[Silent-Sync] Version check failed:', error);
    }
  }, [API_URL, performUpdate]);

  const manualUpdate = useCallback(() => {
    console.log('[Auto-Update] Manual update triggered');
    performUpdate();
  }, [performUpdate]);

  useEffect(() => {
    if (SILENT_MODE) {
      console.log('[Silent-Sync] Background sync ENABLED with anti-loop protection');
    }
    
    // Immediate check on page load
    checkVersion();

    // Check version every 60 seconds
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check when online (network restored)
    const handleOnline = () => {
      checkVersion();
    };
    window.addEventListener('online', handleOnline);

    // Check on window focus (user clicks on window)
    const handleFocus = () => {
      checkVersion();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);

  return {
    updateAvailable,
    isUpdating,
    manualUpdate,
    currentVersion: CURRENT_VERSION
  };
}
